<template>
  <v-container v-if="builder">
    <v-layout row wrap>
      <v-toolbar flat dark class="primary mb-3">
        <v-icon large>home</v-icon>
        <v-toolbar-title class="display-1">{{ builder.company_name }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn to="/builders" outline>View Builder List</v-btn>
        <v-btn @click="editBuilderDialog = true" outline color="white"><v-icon left dark>edit</v-icon>Edit</v-btn>
      </v-toolbar>
      <v-flex xs12 md5 class="pr-2 mb-4">
        <!-- Address Information -->
        <v-toolbar v-if="builder.address || builder.city || builder.state || builder.zip" flat height="50px" dark class="primary">
          <v-icon>location_on</v-icon>
          <v-toolbar-title>Builder Information</v-toolbar-title>
        </v-toolbar>
        <v-list v-if="builder.address || builder.city || builder.state || builder.zip">
          <v-list-tile @click="builderLinks('address', builder.address + '-' + builder.zip)" v-if="builder.address || builder.city || builder.state || builder.zip" class="mt-2 mb-2">
            <v-list-tile-action>
              <v-icon color="indigo">location_on</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{ builder.address }}</v-list-tile-title>
              <v-list-tile-sub-title>{{ builder.city }}, {{ builder.state }} {{ builder.zip }}</v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
        <v-list v-if="builder.phone_1">
          <v-list-tile @click="builderLinks('phone', builder.phone_1)" class="mt-2 mb-2">
            <v-list-tile-action>
              <v-icon color="indigo">phone</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{ formatPhoneNumber(builder.phone_1) }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </v-list>
        <!-- Builder Contacts -->
        <v-toolbar v-if="builderContacts" flat height="50px" dark class="mt-3 primary">
          <v-icon>people</v-icon>
          <v-toolbar-title>Builder Contacts</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn @click="addBuilderDialog = true" flat small color="white"><v-icon left dark>add_circle_outline</v-icon>Add Contact</v-btn>
        </v-toolbar>
        <v-list v-for="(contact, index) in builderContacts" :key="index">
          <v-list-tile>
            <v-list-tile-action>
              <v-icon color="primary">person</v-icon>
            </v-list-tile-action>
            <v-list-tile-title class="title">{{ contact.first_name }} {{ contact.last_name }}<span class="body-2">{{ contact.title ? ', ' + contact.title : '' }}</span></v-list-tile-title>
            <v-spacer></v-spacer>
            <v-icon class="pr-3" style="cursor: pointer" @click="openEditContactDialog(index)">create</v-icon>
            <v-icon style="cursor: pointer" @click="deleteBuilderContact(index)">delete</v-icon>
          </v-list-tile>
          <v-layout row wrap>
          <div class="col1" v-if="contact.phone">
            <v-list-tile @click="builderLinks('phone', contact.phone)">
              <v-list-tile-action>
                <v-icon color="primary">phone</v-icon>
              </v-list-tile-action>
              <v-list-tile-title>{{ formatPhoneNumber(contact.phone) }}</v-list-tile-title>
            </v-list-tile>
            </div>
          <div class="flex-grid">
            <div class="col1" v-if="contact.email">
              <v-list-tile @click="builderLinks('email', contact.email)">
                <v-list-tile-action>
                  <v-icon color="primary">email</v-icon>
                </v-list-tile-action>
                <v-list-tile-title>{{ contact.email }}</v-list-tile-title>
              </v-list-tile>
            </div>
          </div>
          </v-layout>
          <v-divider inset></v-divider>
      </v-list>
      </v-flex>
      <!-- Projects Information -->
      <!-- Active Projects -->
      <v-layout column>
        <v-flex pl-4 md7 xs12>
          <v-card>
            <v-toolbar flat dark color="cyan">
              <v-icon medium dark class="pl-2">ballot</v-icon>
              <v-toolbar-title>Active Projects</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn dark flat @click="createProjectDialog = true" @closeDialog="createProjectDialog = true">
            </v-btn>
            </v-toolbar>
              <v-list two-line>
                <div v-if="activeProjects.length < 1" class="ml-5">No Active Projects</div>
                <template v-for="(project) in activeProjects">
                  <v-container :key="project.name" style="cursor: pointer" @click="builderLinks('project', project.id)">
                    <v-layout>
                      <v-flex xs12>
                        <p class="metrics title">{{ project.name }} #{{ project.job_number }}</p>
                        <p class="caption grey--text">{{ project.address }} {{ project.city }}, {{ project.state }} {{ project.zip }} | Gate Code: {{ project.gate_code }}</p>
                        <p class="caption black--text font-weight-bold">{{ project.status }}</p>
                      </v-flex>
                    </v-layout>
                    <v-layout row wrap justify-space-around>
                      <v-flex xs3>
                        <p class="metrics primary--text display-1 text-xs-center">{{project.completedTasks}} <span class="caption">of</span> {{project.totalTasks}}</p>
                        <p class="caption text-xs-center">Tasks Completed</p>
                      </v-flex>
                      <v-flex xs3>
                        <p class="metrics red--text display-1 text-xs-center">{{project.pastDueTasks}}<span class="caption"></span></p>
                        <p class="caption text-xs-center">Tasks Past Due</p>
                      </v-flex>
                      <v-flex xs12>
                        <div class="w3-light-grey w3-round-xlarge" style="width:100%">
                          <div class="w3-container w3-blue w3-round-xlarge" :style="'width:' + project.progress + '%'">{{project.progress}}%</div>
                        </div>
                      </v-flex>
                    </v-layout>
                    <v-divider class="mt-4"></v-divider>
                  </v-container>
                </template>
            </v-list>
          </v-card>
        </v-flex>
        <!-- End: Completed Projects -->
        <v-flex pl-4 md7 xs12>
          <v-card>
            <v-toolbar flat dark color="success">
              <v-icon medium dark class="pl-2">ballot</v-icon>
              <v-toolbar-title>Completed Projects</v-toolbar-title>
            </v-toolbar>
              <v-list two-line>
                <div v-if="completedProjects.length < 1" class="ml-5">No Completed Projects</div>
                <template v-for="(project) in completedProjects">
                  <v-container :key="project.name" style="cursor: pointer" @click="builderLinks('project', project.id)">
                    <v-layout>
                      <v-flex xs12>
                        <p class="metrics title">{{ project.name }} #{{ project.job_number }}</p>
                        <p class="caption grey--text">{{ project.address }} {{ project.city }}, {{ project.state }} {{ project.zip }} | Gate Code: {{ project.gate_code }}</p>
                        <p class="caption black--text font-weight-bold">{{ project.status }}</p>
                      </v-flex>
                    </v-layout>
                    <v-divider class="mt-4"></v-divider>
                  </v-container>
                </template>
            </v-list>
          </v-card>
        </v-flex>
      </v-layout>
      <!-- End: Projects Section -->
    </v-layout>
  <!-- Add Builder Contact Dialog -->
    <v-dialog max-width="800px" persistent v-model="addBuilderDialog" >
      <template>
        <v-toolbar dark flat class="secondary headline mb-0">
          <v-toolbar-title>Add Builder Contact</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn class="ml-4" dark flat icon @click="closeAddDialog"><v-icon>cancel</v-icon></v-btn>
        </v-toolbar>
        <v-container class="white">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-layout>
              <v-flex md6 xs12><v-text-field v-model="addContact.first_name" :rules="nameRules" :counter="30" label="First Name" required prepend-icon="person"></v-text-field></v-flex>
              <v-flex md6 xs12><v-text-field v-model="addContact.last_name" :rules="nameRules" :counter="30" label="Last Name" required prepend-icon="person"></v-text-field></v-flex>
            </v-layout>
            <!-- <v-layout>
              <v-flex md6 xs12><v-text-field v-model="addContact.title" :counter="30" label="Title" required prepend-icon="person"></v-text-field></v-flex>
            </v-layout> -->
            <v-layout>
              <v-flex md6 xs12><v-text-field v-model="addContact.email" :rules="emailRules" label="Email" required prepend-icon="email"></v-text-field></v-flex>
              <v-flex md6 xs12><v-text-field v-model="addContact.phone" label="Phone" mask="phone" required prepend-icon="phone"></v-text-field></v-flex>
            </v-layout>
            <v-flex md6 xs12>
              <v-layout row>
                <v-select v-model="addContact.title" :items="builderTitles" label="Title" clearable chip prepend-icon="account_circle"></v-select>
                <v-btn v-if="user.permissions.others.manageBuilders" flat icon @click="editDropdownDialog = true"><v-icon>edit</v-icon></v-btn>
              </v-layout>
            </v-flex>
            <v-btn class="primary" :disabled="!valid" @click="saveAndAdd">Save and Close</v-btn>
            <v-btn class="secondary" @click="closeAddDialog">Cancel</v-btn>
          </v-form>
        </v-container>
      </template>
    </v-dialog>
  <!-- End: Add Builder Contact Dialog -->
  <!-- Edit Builder Dialog -->
    <v-dialog max-width="800px" persistent v-model="editBuilderDialog" v-if="editBuilderDialog">
      <template>
        <v-toolbar dark flat class="secondary">
          <v-toolbar-title>Edit Builder</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn @click="deleteBuilder" outline class="red red--text">Delete Builder</v-btn>
          <v-btn class="ml-4" dark flat icon @click="editBuilderDialog = false"><v-icon>cancel</v-icon></v-btn>
        </v-toolbar>
        <v-container class="white pt-3">
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-layout row wrap>
              <v-flex md6 xs12><v-text-field v-model="editBuilder.company_name" :rules="nameRules" :counter="30" label="Company Name" required prepend-icon="person"></v-text-field></v-flex>
              <v-flex md6 xs12>
                  <v-text-field v-model="editBuilder.phone_1" mask="phone" name="phone" label="Phone" prepend-icon="phone"></v-text-field>
              </v-flex>
              <!-- Address Information -->
                <v-layout row wrap>
                <v-flex xs12>
                  <v-text-field v-model="editBuilderAddress.address" clearable name="address" label="Address" prepend-icon="add_location"></v-text-field>
                </v-flex>
                <v-flex sm6 xs12>
                  <v-text-field v-model="editBuilderAddress.city" clearable name="city" label="City" prepend-icon=" "></v-text-field>
                </v-flex>
                <v-flex sm3 xs6>
                  <v-text-field v-model="editBuilderAddress.state" clearable name="state" label="State (i.e. FL)" prepend-icon=" "></v-text-field>
                </v-flex>
                <v-flex sm3 xs6>
                  <v-text-field v-model="editBuilderAddress.zip" clearable name="zip" label="Zip"></v-text-field>
                </v-flex>
                </v-layout>
              </v-layout>
            <v-btn class="primary" :disabled="!valid" @click="editBuilderAndSave">Save and Close</v-btn>
            <v-btn class="secondary" @click="editBuilderDialog = false">Cancel</v-btn>
          </v-form>
        </v-container>
      </template>
    </v-dialog>
  <!-- End: Edit Builder Dialog -->
  <!-- Edit Builder Contact Dialog -->
    <v-dialog max-width="800px" persistent v-model="editBuilderContactDialog" v-if="editBuilderContactDialog">
      <template>
        <v-toolbar dark flat class="secondary headline mb-0">
          <v-toolbar-title>Edit Builder Contact</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn class="ml-4" dark flat icon @click="editBuilderContactDialog = false"><v-icon>cancel</v-icon></v-btn>
        </v-toolbar>
        <v-container class="white">
          <v-divider></v-divider>
          <v-form ref="formEdit" v-model="valid" lazy-validation>
            <v-layout>
              <v-flex md6 xs12><v-text-field v-model="editContact.first_name" :rules="nameRules" :counter="30" label="First Name" required prepend-icon="person"></v-text-field></v-flex>
              <v-flex md6 xs12><v-text-field v-model="editContact.last_name" :rules="nameRules" :counter="30" label="Last Name" required prepend-icon="person"></v-text-field></v-flex>
            </v-layout>
            <!-- <v-layout>
              <v-flex xs12><v-text-field v-model="editContact.title" :counter="30" label="Title" required prepend-icon="person"></v-text-field></v-flex>
            </v-layout> -->
            <v-layout>
              <v-flex md6 xs12><v-text-field v-model="editContact.phone" label="Phone" mask="phone" required prepend-icon="phone"></v-text-field></v-flex>
              <v-flex md6 xs12><v-text-field v-model="editContact.email" :rules="emailRules" label="Email" required prepend-icon="email"></v-text-field></v-flex>
            </v-layout>
            <v-flex md6 xs12>
              <v-layout row>
                <v-select v-model="editContact.title" :items="builderTitles" label="Title" clearable chip prepend-icon="account_circle"></v-select>
                <v-btn v-if="user.permissions.others.manageBuilders" flat icon @click="editDropdownDialog = true"><v-icon>edit</v-icon></v-btn>
              </v-layout>
            </v-flex>
            <v-btn class="primary" :disabled="!valid" @click="saveBuilderContactEdit(editContact.index)">Save and Close</v-btn>
            <v-btn class="secondary" @click="editBuilderContactDialog = false">Cancel</v-btn>
          </v-form>
        </v-container>
      </template>
    </v-dialog>
  <!-- End: Edit Builder Contact Dialog -->
  <!-- Edit Drop Down -->
  <v-dialog max-width="600px" v-model="editDropdownDialog">
    <edit-dropdown v-if="editDropdownDialog" dropdownName="builderTitles" @closeDialog="editDropdownDialog = false"/>
  </v-dialog>
  <!-- End: Edit Drop Down -->
  </v-container> 
</template>

<script>
import { db } from '../../firebaseInit'
import _ from 'lodash'
import EditDropdown from '../../helpers/EditDropdown'

export default {
  props: ['id'],
  data () {
    return {
      editDropdownDialog: null,
      editBuilderDialog: null,
      addBuilderDialog: null,
      editBuilderContactDialog: null,
      editContact: null,
      builder: null,
      editBuilder: {company_name: null, phone_1: null},
      editBuilderAddress: {address: null, city: null, state: null, zip: null},
      builderContacts: [],
      valid: true,
      addContact: {first_name: null, last_name: null, email: null, phone: null, deleted: false, title: null},
      nameRules: [
        v => !!v || 'Field is required',
        v => (v && v.length <= 30) || 'Name must be less than 30 characters'
      ],
      emailRules: [
        // v => !!v || 'E-mail is required',
        v => v ? /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || 'E-mail must be valid' : true
      ]
    }
  },
  mounted () {
    this.getBuilder()
  },
  computed: {
    user () {
      return this.$store.getters.user
    },
    builderTitles () {
      return this.$store.getters.builderTitles
    },
    activeProjects () {
      return this.projects.filter(f => f.status !== 'Completed')
    },
    completedProjects () {
      return this.projects.filter(f => f.status === 'Completed')
    },
    projects () {
      const getProjects = _.orderBy(this.$store.getters.builderProjects, 'created_date', 'desc')
      let projectList = []
      getProjects.forEach(project => {
        const status = project.status
        if (project.tasks && project.tasks.length > 0) {
          const allTasks = project.tasks.filter(f => f.deleted === false)
          const totalTasks = allTasks.length
          const completedTasks = allTasks.filter(f => f.type === status && (f.status === 'Completed' || f.status === 'Not Applicable')).length
          const pastDueTasks = allTasks.filter(f => f.type === status && f.due_date.seconds < new Date().getTime() / 1000 && f.status !== 'Completed' && f.status !== 'Not Applicable').length
          const progress = totalTasks === 0 || completedTasks === 0 ? 0 : Math.round(completedTasks / totalTasks * 100)
          projectList.push({...project, totalTasks, completedTasks, pastDueTasks, progress})
        } else {
          projectList.push({...project, totalTasks: 0, completedTasks: 0, progress: 0, pastDueTasks: 0})
        }
      })
      return projectList
    }
  },
  methods: {
    getProjects () {
      this.$store.dispatch('builderProjects', {builder: this.builder.company_name})
    },
    deleteBuilder (item) {
      confirm('Are you sure you want to delete the Builder ' + this.builder.company_name + '?') &&
      this.$store.dispatch('deleteBuilder', { id: this.builder.id, company_name: this.builder.company_name }) &&
      this.$router.push('/builders')
    },
    editBuilderAndSave () {
      if (this.$refs.form.validate()) {
        if (this.editBuilderAddress.address || this.editBuilderAddress.city || this.editBuilderAddress.state || this.editBuilderAddress.zip || this.builder.address || this.builder.city || this.builder.state || this.builder.zip) {
          this.editBuilder = {...this.editBuilder, ...this.editBuilderAddress}
        }
        // Find Changes
        const difference = Object.keys(this.editBuilder).filter(k => this.editBuilder[k] !== this.builder[k])
        let previousState = {}
        let newState = {}
        difference.forEach(item => {
          previousState = {...previousState, [item]: this.builder[item]}
        })
        difference.forEach(item => {
          newState = {...newState, [item]: this.editBuilder[item]}
          this.builder = {...this.builder, [item]: this.editBuilder[item]}
        })
        const payload = {previousState: previousState, newState: newState, id: this.builder.id, company_name: this.editBuilder.company_name}
        this.$store.dispatch('editBuilder', payload)
        this.editBuilderDialog = false
      }
    },
    getBuilderAddress () {
      this.editBuilder.company_name = this.builder.company_name
      this.editBuilder.phone_1 = this.builder.phone_1
      this.editBuilderAddress.address = this.builder.address
      this.editBuilderAddress.city = this.builder.city
      this.editBuilderAddress.state = this.builder.state
      this.editBuilderAddress.zip = this.builder.zip
    },
    saveBuilderContactEdit (index) {
      if (this.$refs.formEdit.validate()) {
        const from = this.builderContacts[index]
        delete this.editContact.index
        this.builderContacts.splice(index, 1)
        this.builderContacts.push(this.editContact)
        const to = this.editContact
        this.editBuilderContactDialog = false
        const payload = { array: this.builderContacts, from: from, to: to, id: this.builder.id, builderContact: this.editContact, companyName: this.builder }
        this.$store.dispatch('editBuilderContact', payload)
      }
    },
    getBuilder () {
      db.collection('contacts').doc(this.id).get()
        .then(doc => {
          this.builder = {...doc.data(), id: doc.id}
        })
        .then(() => {
          this.getBuilderContacts()
          this.getBuilderAddress()
          this.getProjects()
        })
        .catch(error => console.log('Could not load builder data: ', error))
    },
    getBuilderContacts () {
      const newArray = this.builder.builder_contacts.filter(contact => {
        return contact.deleted !== true
      })
      newArray.forEach(item => {
        this.builderContacts.push(item)
      })
    },
    formatPhoneNumber (value) {
      let s2 = ('' + value).replace(/\D/g, '')
      let m = s2.match(/^(\d{3})(\d{3})(\d{4})$/)
      return (!m) ? null : '(' + m[1] + ')' + ' ' + m[2] + '-' + m[3]
    },
    builderLinks (type, value) {
      // window.open('mailto: ' + value)
      if (type === 'email') {
        location.href = 'mailto: ' + value
      }
      if (type === 'phone') {
        location.href = 'tel:' + value
      }
      if (type === 'address') {
        window.open('https://www.google.com/maps?q=' + value)
      }
      if (type === 'project') {
        this.$router.push('/project/' + value)
      }
    },
    builderChange (value) {
      this.builder = {...this.builder, ...value}
    },
    closeAddDialog () {
      this.addBuilderDialog = false
      this.addContact = {first_name: null, last_name: null, email: null, phone: null, active: true}
    },
    saveAndAdd () {
      if (this.$refs.form.validate()) {
        this.builderContacts.push(this.addContact)
        const payload = {arrayPayload: this.builderContacts, newContact: this.addContact, id: this.builder.id}
        this.$store.dispatch('addBuilderContact', payload)
        this.closeAddDialog()
      }
    },
    deleteBuilderContact (index) {
      confirm('Are you sure you want to delete this contact?') && this.executeDelete(index)
    },
    executeDelete (index) {
      this.builderContacts[index].deleted = true
      const deletedContact = this.builderContacts[index]
      const payload = {contactArray: this.builderContacts, deletedContact: deletedContact, index: index, id: this.builder.id, companyName: this.builder.company_name}
      this.$store.dispatch('deleteBuilderContact', payload)
      this.builderContacts.splice(index, 1)
    },
    openEditContactDialog (index) {
      this.editContact = this.builderContacts[index]
      this.editContact = {...this.editContact, index: index}
      this.editBuilderContactDialog = true
    }
  },
  components: {
    EditDropdown
  }
}
</script>

<style>
.metrics {
  margin-bottom: 0px !important;
}
.w3-light-grey,
.w3-hover-light-grey:hover,
.w3-light-gray,
.w3-hover-light-gray:hover {
  color: #000 !important;
  background-color: #f1f1f1 !important;
  width: 100%;
}
.w3-round-xlarge {
  border-radius: 16px;
}
.w3-blue,
.w3-hover-blue:hover {
  color: #fff !important;
  background-color: #2196f3 !important;
}
.w3-container,
.w3-panel {
  padding: 0.01em 16px;
}
.w3-round-xlarge {
  border-radius: 16px;
}
.flex-grid {
  display: flex;
}
.col {
  flex: 1;
}
</style>


// WEBPACK FOOTER //
// src/components/builders/Builder.vue